import type { Document } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Badge } from '../../components/ui/badge';
import { Eye, FileText, Calendar, User, Folder, Tag, Info } from 'lucide-react';
import { DetailField, DetailLabel } from './DocumentDetailFields';

interface DocumentDetailsTabProps {
  document: Document;
  /** 'Public' / 'Shared' / 'Private', derived once by the modal for the header badge too. */
  visibility: string;
  /** Date of the version on screen now (falls back to uploaded_at for pre-tracking rows). */
  currentVersionDate: string | undefined;
  /** True once at least one earlier version exists, which unlocks "Originally uploaded". */
  hasPreviousVersions: boolean;
}

/** Details tab body of DocumentDetailModal: description, metadata grid, tags, external URL. */
export default function DocumentDetailsTab({
  document,
  visibility,
  currentVersionDate,
  hasPreviousVersions,
}: DocumentDetailsTabProps) {
  const { formatDate } = useFormatters();
  const cleanedDescription = (document.description || '').trim();

  return (
    <div className="space-y-4">
      {cleanedDescription && (
        <div>
          <DetailLabel icon={Info} text="Description" />
          <p className="text-sm whitespace-pre-wrap break-words">{cleanedDescription}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <DetailField
          icon={Folder}
          label="Folder"
          value={document.category_name || 'Uncategorized'}
        />
        <DetailField
          icon={FileText}
          label="Version"
          value={document.version || '—'}
        />
        <DetailField
          icon={User}
          label="Uploaded by"
          value={
            document.version_uploaded_by_name
            || document.uploaded_by_name
            || '—'
          }
        />
        <DetailField
          icon={Calendar}
          label="Uploaded at"
          value={currentVersionDate ? formatDate(currentVersionDate) : '—'}
        />
        {/* Only meaningful once the file has actually been revised —
            on a never-versioned document it would just repeat the
            field above. */}
        {hasPreviousVersions && (
          <DetailField
            icon={Calendar}
            label="Originally uploaded"
            value={document.uploaded_at ? formatDate(document.uploaded_at) : '—'}
          />
        )}
        <DetailField
          icon={Eye}
          label="Visibility"
          value={visibility}
        />
        <DetailField
          icon={FileText}
          label="File type"
          value={(document.file_extension || document.document_type || '—').toUpperCase()}
        />
      </div>

      {document.tags && (
        <div>
          <DetailLabel icon={Tag} text="Tags" />
          <div className="flex flex-wrap gap-1.5">
            {document.tags.split(',').map((t, i) => {
              const tag = t.trim();
              if (!tag) return null;
              return (
                <Badge key={i} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {document.document_type === 'url' && document.external_url && (
        <div>
          <DetailLabel icon={Info} text="External URL" />
          <a
            href={document.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline break-all"
          >
            {document.external_url}
          </a>
        </div>
      )}
    </div>
  );
}
